import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, PanelBody } from '@/components/ui';
import { api } from '@/lib/api';
import { formatDecimalString } from '@/lib/format';
import type { InvoiceCreditSummary as Summary } from '@/lib/types';

/**
 * What has been taken off an invoice: one line per applied credit note (with its
 * number), then payments received, then the amount due.
 *
 * It is SUPPLEMENTARY to the totals beside it, so it renders nothing while
 * loading (no skeleton jump under the grand total), nothing on a failure, and
 * nothing against an API older than `/credit-summary` (a 404).
 *
 * ⚠️ `credits: null` means the reader may not read credit notes — say nothing
 * about credit, never "no credit". `amountPaid` / `amountDue` `null` means Xero
 * was never asked — no line at all, never a zero.
 */
export function InvoiceCreditSummary({
  invoiceId,
  reloadKey = 0,
  variant = 'plain',
}: {
  invoiceId: number;
  /** Bump to refetch, e.g. after credit is applied. */
  reloadKey?: number;
  /** `panel` sits as its own block on the detail page; `plain` inside a form section. */
  variant?: 'panel' | 'plain';
}) {
  const [summary, setSummary] = useState<Summary | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .invoiceCreditSummary(invoiceId, controller.signal)
      .then((response) => {
        if (!controller.signal.aborted) setSummary(response.data);
      })
      .catch(() => {
        // A 404 (older API), a refusal or a network failure all render nothing.
        if (!controller.signal.aborted) setSummary(null);
      });
    return () => controller.abort();
  }, [invoiceId, reloadKey]);

  if (summary === null || summary.invoiceId !== invoiceId) return null;

  const credits = summary.credits ?? [];
  const paid = isPositiveDecimal(summary.amountPaid) ? summary.amountPaid : null;
  const due = summary.amountDue;
  if (credits.length === 0 && paid === null && due === null) return null;

  const currency = summary.currency?.trim() || null;

  const rows = (
    <dl className="grid max-w-md gap-2">
      {credits.map((credit) => (
        <div key={credit.allocationId} className="flex items-baseline justify-between gap-3">
          <dt className="min-w-0 text-sm text-fg-secondary [overflow-wrap:anywhere]">
            Less credit note{' '}
            <Link
              to={`/credit-notes/${credit.creditNoteId}`}
              className={`${credit.creditNoteNumber?.trim() ? 'whitespace-nowrap ' : ''}font-medium text-fg underline decoration-border underline-offset-2 transition-colors duration-150 ease-out hover:decoration-fg motion-reduce:transition-none`}
            >
              {credit.creditNoteNumber?.trim() || credit.creditNoteSubject}
            </Link>
            {!credit.inXero && (
              <Badge tone="neutral" className="ml-2 align-middle">
                Not in Xero
              </Badge>
            )}
          </dt>
          <dd className="shrink-0 text-sm text-fg">
            <Deduction amount={credit.amount} currency={currency} />
          </dd>
        </div>
      ))}
      {paid !== null && (
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-sm text-fg-secondary">Less payments received</dt>
          <dd className="shrink-0 text-sm text-fg">
            <Deduction amount={paid} currency={currency} />
          </dd>
        </div>
      )}
      {due !== null && (
        <div className="flex items-baseline justify-between gap-3 border-t border-border pt-2">
          <dt className="text-sm font-semibold text-fg">Amount due</dt>
          <dd className="shrink-0 text-sm font-semibold text-fg">
            <Figure amount={due} currency={currency} />
          </dd>
        </div>
      )}
    </dl>
  );

  if (variant === 'panel') {
    return <PanelBody className="border-b border-border last:border-b-0">{rows}</PanelBody>;
  }
  return <div className="mt-3 border-t border-border pt-3">{rows}</div>;
}

/** A positive decimal string; `null`, blank, zero and negatives are not. */
function isPositiveDecimal(value: string | null): value is string {
  if (value === null) return false;
  const trimmed = value.trim();
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return false;
  return /[1-9]/.test(trimmed);
}

function Figure({ amount, currency }: { amount: string; currency: string | null }) {
  return (
    <span className="tabular-nums whitespace-nowrap">
      {formatDecimalString(amount)}
      {currency ? <span className="ml-1 text-[0.85em] text-fg-muted">{currency}</span> : null}
    </span>
  );
}

/** A figure taken off the invoice, shown in parentheses: `(2,450.00) USD`. */
function Deduction({ amount, currency }: { amount: string; currency: string | null }) {
  const text = formatDecimalString(amount.trim().replace(/^-/, ''));
  return (
    <span className="tabular-nums whitespace-nowrap">
      ({text})
      {currency ? <span className="ml-1 text-[0.85em] text-fg-muted">{currency}</span> : null}
    </span>
  );
}
